import * as React from "react";

import { SimpleMarkdown } from "../../../components/SimpleMarkdown";
import { DocumentSourceRenderer } from "../renderers/DocumentSourceRenderer";
import { SourceHeader } from "./SourceHeader";
import { TableSourceRenderer } from "../renderers/TableSourceRenderer";
import { findCitationById } from "../../ai/findCitation";
import { extractSourceItemsFromCitations } from "../../ai/sourceItems";
import type { SourceItem } from "../../ai/sourceItems";
import type { Citation, CitationLookupResult } from "../../types";
import { SOURCE_CLIENT_LABELS } from "../colors";

interface CitationContentProps {
  /** The inline `<u id="…">` uuid that triggered the drawer. */
  citationId: string | null;
  /**
   * Fully-resolved source item (preferred path when the drawer was
   * opened from a Retrieved-sources / Citations card click). When this
   * is set we render directly from it and ignore `citationId`.
   */
  sourceItem?: SourceItem | null;
  /** The list of citations from this message turn. */
  citations: Citation[];
  allowIframeSameOrigin?: boolean;
}

/**
 * Body of the source-information drawer. Two ways in:
 *
 *   1. Inline underline click → `citationId` is a uuid, we resolve it
 *      against `citations` to find the parent envelope + matching
 *      link/row. We then try to upgrade that to a `SourceItem` so the
 *      drawer can render the *whole* source (the full SEC iframe, the
 *      full FactSet table, the full web-page markdown) rather than
 *      the per-uuid excerpt — that matches the finster-chat experience
 *      where clicking a citation opens the document, not just the row.
 *
 *   2. Card "View source" click → `sourceItem` is set directly.
 *
 * Per source type we render:
 *
 *   - **SEC filings** — embed the highlighted `<iframe>` in an
 *     isolated sandbox so the Google Docs Viewer can scroll to the
 *     highlighted span. Falls back to a link if the iframe HTML is
 *     missing.
 *   - **Web Search / Company Website / Industry Research / IR** —
 *     render the markdown page dump through `SimpleMarkdown` and a
 *     prominent "Visit source" footer.
 *   - **FactSet Fundamentals / Estimates / Market Data** — render the
 *     full markdown table (not just the cited row) so the user can
 *     see the surrounding columns for context. Highlights the cited
 *     row when we know which uuid triggered the drawer.
 *   - **Fallback** — JSON pretty-print, collapsed by default.
 */
export const CitationContent: React.FC<CitationContentProps> = ({
  citationId,
  sourceItem,
  citations,
  allowIframeSameOrigin = true,
}) => {
  // Resolve the drawer target. If a source item was passed, use it
  // directly. Otherwise try to back-resolve from `citationId` → parent
  // envelope → source item.
  const resolved = React.useMemo(() => {
    if (sourceItem) {
      return { item: sourceItem, lookup: null as CitationLookupResult | null };
    }
    if (!citationId) {
      return { item: null as SourceItem | null, lookup: null };
    }
    const lookup = findCitationById(citations, citationId);
    const parent = findParentEnvelope(citations, citationId);
    if (!parent) {
      return { item: null, lookup };
    }
    const items = extractSourceItemsFromCitations([parent], {
      clientLabels: SOURCE_CLIENT_LABELS,
    });
    // Pick the item that contains this uuid. For dictionary citations
    // that means the link whose text references the uuid (or whose
    // key is the uuid). For markdown citations there's exactly one
    // item per envelope.
    const matched =
      items.find((it) => {
        if (it.kind === "table") return true;
        return it.citedUuids.includes(citationId) || it.id.includes(citationId);
      }) ?? items[0];
    return { item: matched ?? null, lookup };
  }, [sourceItem, citationId, citations]);

  if (!resolved.item) {
    // Nothing we can do — render the legacy fallback.
    return (
      <div className="flex flex-col gap-3 text-[13px] leading-relaxed text-foreground">
        <p className="text-[12px] text-muted-foreground">
          No structured source available for this citation.
        </p>
        {resolved.lookup?.content && (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
            <SimpleMarkdown text={resolved.lookup.content} />
          </div>
        )}
      </div>
    );
  }

  const item = resolved.item;
  const activeHighlightUuid =
    citationId ?? (sourceItem ? sourceItem.citedUuids[0] ?? null : null);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 text-[13px] leading-relaxed text-foreground">
      <SourceHeader item={item} />
      {item.kind === "document" ? (
        <DocumentSourceRenderer
          item={item}
          highlightUuid={activeHighlightUuid}
          allowIframeSameOrigin={allowIframeSameOrigin}
        />
      ) : (
        <TableSourceRenderer item={item} highlightUuid={activeHighlightUuid} />
      )}
    </div>
  );
};

function findParentEnvelope(citations: Citation[], uuid: string): Citation | undefined {
  for (const cand of citations) {
    const data = cand.citationData ?? cand.citation_data;
    if (typeof data === "string" && data.includes(uuid)) return cand;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const keys = Object.keys(data);
      if (keys.includes(uuid)) return cand;
      for (const key of keys) {
        const v = (data as Record<string, { text?: unknown }>)[key];
        if (v && typeof v === "object" && typeof v.text === "string" && v.text.includes(uuid)) {
          return cand;
        }
      }
    }
  }
  return undefined;
}
